import { tagMessage } from './tagging';

export type Poster<From extends Entity, To extends Entity> = (
  message: ValidMessages[To],
  target: Targets[From][To],
  options: MessageOptions,
) => TaggedMessage<Entity>;

export type Targets = {
  mainPage: {
    mainPage: Window;
    securityFrame: Window;
    blockchainWorker: Worker;
    databaseWorker: Worker;
  };
  securityFrame: {
    mainPage: Window;
    securityFrame: never;
    blockchainWorker: never;
    databaseWorker: never;
  };
  blockchainWorker: {
    mainPage: Window;
    securityFrame: never;
    blockchainWorker: never;
    databaseWorker: MessagePort;
  };
  databaseWorker: {
    mainPage: Window;
    securityFrame: never;
    blockchainWorker: MessagePort;
    databaseWorker: never;
  };
};

// Allowed communication paths:
//
//                 Security Frame
//                       |
//                       |
//                   Main Page
//                    /     \
//                   /       \
//    Blockchain Worker --- Database Worker

export const POSTERS: {
  [From in Entity]: Partial<{
    [To in Entity]: Poster<From, To>;
  }>;
} = {
  mainPage: {
    mainPage: sendSelfMessageToMainPage,
    securityFrame: postMessageFromMainPageToSecurityFrame,
    blockchainWorker: postMessageFromMainPageToBlockchainWorker,
    databaseWorker: postMessageFromMainPageToDatabaseWorker,
  },
  securityFrame: {
    mainPage: postMessageFromSecurityFrameToMainPage,
  },
  blockchainWorker: {
    mainPage: postMessageFromBlockchainWorkerToMainPage,
    databaseWorker: postMessageFromBlockchainWorkerToDatabaseWorker,
  },
  databaseWorker: {
    mainPage: postMessageFromDatabaseWorkerToMainPage,
    blockchainWorker: postMessageFromDatabaseWorkerToBlockchainWorker,
  },
};

/// /////////////////////////////////////////////////////////////////////////////
// Main Page -> Main Page (self-send, for testing)

function sendSelfMessageToMainPage(
  message: MainPageMessage,
  target: Window,
  options: MessageOptions,
) {
  const taggedMessage = tagMessage('mainPage', 'mainPage', message, options);
  target.postMessage(taggedMessage);
  return taggedMessage;
}

/// /////////////////////////////////////////////////////////////////////////////
// Main Page <-> Security Frame

function postMessageFromMainPageToSecurityFrame(
  message: SecurityFrameMessage,
  target: Window,
  options: MessageOptions,
) {
  const taggedMessage = tagMessage('mainPage', 'securityFrame', message, options);
  target.postMessage(taggedMessage);
  return taggedMessage;
}

function postMessageFromSecurityFrameToMainPage(
  message: MainPageMessage,
  target: Window,
  options: MessageOptions,
) {
  const taggedMessage = tagMessage('securityFrame', 'mainPage', message, options);
  target.postMessage(taggedMessage);
  return taggedMessage;
}

/// /////////////////////////////////////////////////////////////////////////////
// Main Page <-> Blockchain Worker

function postMessageFromMainPageToBlockchainWorker(
  message: BlockchainWorkerMessage,
  target: Worker,
  options: MessageOptions,
) {
  const taggedMessage = tagMessage('mainPage', 'blockchainWorker', message, options);
  const transfers = message.kind === 'SETUP' ? [message.databaseWorkerPort] : [];
  target.postMessage(taggedMessage, transfers);
  return taggedMessage;
}

function postMessageFromBlockchainWorkerToMainPage(
  message: MainPageMessage,
  target: Window,
  options: MessageOptions,
) {
  const taggedMessage = tagMessage('blockchainWorker', 'mainPage', message, options);
  target.postMessage(taggedMessage);
  return taggedMessage;
}

/// /////////////////////////////////////////////////////////////////////////////
// Main Page <-> Database Worker

function postMessageFromMainPageToDatabaseWorker(
  message: DatabaseWorkerMessage,
  target: Worker,
  options: MessageOptions,
) {
  const taggedMessage = tagMessage('mainPage', 'databaseWorker', message, options);
  const transfers = message.kind === 'SETUP' ? [message.blockchainWorkerPort] : [];
  target.postMessage(taggedMessage, transfers);
  return taggedMessage;
}

function postMessageFromDatabaseWorkerToMainPage(
  message: MainPageMessage,
  target: Window,
  options: MessageOptions,
) {
  const taggedMessage = tagMessage('databaseWorker', 'mainPage', message, options);
  target.postMessage(taggedMessage);
  return taggedMessage;
}

/// /////////////////////////////////////////////////////////////////////////////
// Blockchain Worker <-> Database Worker

function postMessageFromDatabaseWorkerToBlockchainWorker(
  message: BlockchainWorkerMessage,
  target: MessagePort,
  options: MessageOptions,
) {
  const taggedMessage = tagMessage('databaseWorker', 'blockchainWorker', message, options);
  target.postMessage(taggedMessage);
  return taggedMessage;
}

function postMessageFromBlockchainWorkerToDatabaseWorker(
  message: DatabaseWorkerMessage,
  target: MessagePort,
  options: MessageOptions,
) {
  const taggedMessage = tagMessage('blockchainWorker', 'databaseWorker', message, options);
  target.postMessage(taggedMessage);
  return taggedMessage;
}
